<template>
  <form @submit.prevent="onSubmit" class="tw-w-full">
    <div class="tw-grid tw-grid-cols-1 xl:tw-grid-cols-12 tw-gap-8">
      <!-- Left column -->
      <div class="xl:tw-col-span-8 tw-space-y-6">
        <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
          <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-6">{{ $t('videos.information', 'Video information') }}</h2>

          <div class="tw-space-y-6">
            <FormField
              v-model="form.title"
              type="text"
              :label="$t('videos.title', 'Title')"
              :placeholder="$t('videos.title-placeholder', 'Enter video title')"
              left-icon="solar:videocamera-record-linear"
              :error="errors.title"
              required
            />

            <FormField
              v-model="form.description"
              type="textarea"
              :label="$t('videos.description', 'Description')"
              :placeholder="$t('videos.description-placeholder', 'Describe this video')"
              :error="errors.description"
              rows="5"
            />

            <FormMultiSelect
              v-model="form.categories"
              :options="categoryOptions"
              :label="$t('videos.categories', 'Categories')"
              :placeholder="$t('videos.categories-placeholder', 'Select categories')"
              left-icon="solar:widget-bold-duotone"
              :loading="categoriesLoading"
            />
          </div>
        </div>

        <!-- Source -->
        <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
          <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-6">{{ $t('videos.source', 'Video source') }}</h3>

          <!-- Source type toggle -->
          <div class="tw-flex tw-gap-3 tw-mb-6">
            <button type="button" @click="form.source_type = 'url'"
              :class="[
                'tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-3 tw-rounded-lg tw-border tw-text-sm tw-font-medium tw-transition-colors',
                form.source_type === 'url' ? 'tw-border-primary-500 tw-bg-primary-50 tw-text-primary-700' : 'tw-border-gray-200 tw-text-gray-500 hover:tw-bg-gray-50'
              ]">
              <icon icon="solar:link-bold-duotone" class="tw-text-lg" />
              {{ $t('videos.source-url-option', 'External URL / Embed') }}
            </button>
            <button type="button" @click="form.source_type = 'upload'"
              :class="[
                'tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-3 tw-rounded-lg tw-border tw-text-sm tw-font-medium tw-transition-colors',
                form.source_type === 'upload' ? 'tw-border-primary-500 tw-bg-primary-50 tw-text-primary-700' : 'tw-border-gray-200 tw-text-gray-500 hover:tw-bg-gray-50'
              ]">
              <icon icon="solar:cloud-upload-bold-duotone" class="tw-text-lg" />
              {{ $t('videos.source-upload-option', 'Upload file') }}
            </button>
          </div>

          <!-- URL source -->
          <div v-if="form.source_type === 'url'" class="tw-space-y-6">
            <FormSelect
              v-model="form.provider"
              :options="providerOptions"
              :label="$t('videos.provider', 'Provider')"
              left-icon="solar:playback-speed-bold-duotone"
              :error="errors.provider"
            />
            <FormField
              v-model="form.external_url"
              type="text"
              :label="$t('videos.external-url', 'Video URL')"
              :placeholder="'https://...'"
              left-icon="solar:link-bold-duotone"
              :error="errors.external_url"
              :help="$t('videos.external-url-help', 'YouTube / Vimeo page URL, or a direct .mp4 link')"
            />
          </div>

          <!-- Upload source -->
          <div v-else class="tw-space-y-3">
            <div
              @drop.prevent="handleVideoDrop"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @click="triggerVideoInput"
              :class="[
                'tw-relative tw-w-full tw-min-h-[140px] tw-border-2 tw-border-dashed tw-rounded-lg tw-cursor-pointer tw-transition-all tw-flex tw-items-center tw-justify-center tw-p-6',
                isDragging ? 'tw-border-primary-500 tw-bg-primary-50' : 'tw-border-gray-300 hover:tw-border-primary-400 tw-bg-gray-50'
              ]">
              <div v-if="form.videoFile || existingFile" class="tw-flex tw-items-center tw-gap-3 tw-text-gray-700">
                <icon icon="solar:videocamera-record-bold-duotone" class="tw-text-3xl tw-text-primary-500" />
                <div>
                  <p class="tw-font-medium">{{ form.videoFile ? form.videoFile.name : $t('videos.current-file', 'Current uploaded file') }}</p>
                  <p class="tw-text-xs tw-text-gray-400">{{ $t('videos.click-to-replace', 'Click to replace') }}</p>
                </div>
              </div>
              <div v-else class="tw-flex tw-flex-col tw-items-center tw-text-center">
                <icon icon="solar:cloud-upload-bold-duotone" class="tw-text-5xl tw-text-gray-400 tw-mb-2" />
                <p class="tw-text-sm tw-font-medium tw-text-gray-700">{{ $t('videos.upload-video', 'Upload a video file') }}</p>
                <p class="tw-text-xs tw-text-gray-400 tw-mt-1">{{ $t('videos.upload-hint', 'MP4, MOV, WEBM, AVI, MKV — up to 500MB') }}</p>
              </div>
              <input ref="videoInput" type="file" accept="video/*" @change="handleVideoSelect" class="tw-hidden" />
            </div>
            <p v-if="errors.video" class="tw-text-red-500 tw-text-sm">{{ errors.video }}</p>
          </div>
        </div>
      </div>

      <!-- Right column -->
      <div class="xl:tw-col-span-4 tw-space-y-6">
        <!-- Thumbnail -->
        <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
          <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">{{ $t('videos.thumbnail', 'Thumbnail') }}</h2>
          <div
            @drop.prevent="handleThumbDrop"
            @dragover.prevent="isDraggingThumb = true"
            @dragleave.prevent="isDraggingThumb = false"
            @click="triggerThumbInput"
            :class="[
              'tw-relative tw-w-full tw-h-52 tw-border-2 tw-border-dashed tw-rounded-lg tw-cursor-pointer tw-transition-all tw-flex tw-items-center tw-justify-center',
              isDraggingThumb ? 'tw-border-primary-500 tw-bg-primary-50' : 'tw-border-gray-300 hover:tw-border-primary-400 tw-bg-gray-50',
              form.thumbPreview ? 'tw-border-solid tw-bg-white' : ''
            ]">
            <div v-if="form.thumbPreview" class="tw-relative tw-w-full tw-h-full">
              <img :src="form.thumbPreview" class="tw-w-full tw-h-full tw-object-cover tw-rounded-lg" alt="" />
              <button @click.stop="removeThumb" type="button"
                class="tw-absolute tw-top-2 tw-right-2 tw-w-8 tw-h-8 tw-bg-red-500 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-red-600">
                <icon icon="solar:close-circle-bold" />
              </button>
            </div>
            <div v-else class="tw-flex tw-flex-col tw-items-center tw-text-center tw-p-4">
              <icon icon="solar:gallery-add-bold-duotone" class="tw-text-4xl tw-text-gray-400 tw-mb-2" />
              <p class="tw-text-sm tw-text-gray-600">{{ $t('videos.upload-thumbnail', 'Upload thumbnail') }}</p>
              <p class="tw-text-xs tw-text-gray-400 tw-mt-1">{{ $t('videos.thumbnail-hint', 'JPG, PNG, WEBP — up to 2MB') }}</p>
            </div>
            <input ref="thumbInput" type="file" accept="image/*" @change="handleThumbSelect" class="tw-hidden" />
          </div>
        </div>

        <!-- Settings -->
        <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6 tw-space-y-5">
          <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900">{{ $t('videos.settings', 'Settings') }}</h2>

          <FormField
            v-model="form.duration"
            type="text"
            :label="$t('videos.duration', 'Duration')"
            :placeholder="'e.g. 12:34'"
            left-icon="solar:clock-circle-linear"
          />

          <FormSelect
            v-model="form.status"
            :options="statusOptions"
            :label="$t('videos.status', 'Status')"
            left-icon="solar:playback-speed-bold-duotone"
          />
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="tw-border-t tw-border-gray-200 tw-pt-6 tw-mt-8">
      <div class="tw-flex tw-items-center tw-gap-3">
        <a-button type="button" intent="white" @click="$emit('cancel')" :disabled="saving">
          {{ $t('common.cancel', 'Cancel') }}
        </a-button>
        <a-button
          type="submit"
          intent="primary"
          :loading="saving"
          left-icon="solar:diskette-bold-duotone"
          class="tw-min-w-[180px]"
        >
          {{ submitLabel }}
        </a-button>
      </div>
    </div>
  </form>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch, defineProps, defineEmits, defineExpose } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import FormSelect from '@/components/form/FormSelect.vue';
import FormMultiSelect from '@/components/form/FormMultiSelect.vue';
import appApi from '@/api/app';
import useAlert from '@/composables/useAlert';

const { t } = useI18n();

const props = defineProps({
    video: { type: Object, default: null },
    saving: { type: Boolean, default: false },
    submitLabel: { type: String, default: 'Save' }
});

const emit = defineEmits(['submit', 'cancel']);

const videoInput = ref(null);
const thumbInput = ref(null);
const isDragging = ref(false);
const isDraggingThumb = ref(false);
const categoriesLoading = ref(false);
const categories = ref([]);
const existingFile = ref(false);

const form = reactive({
    title: '',
    description: '',
    categories: [],
    source_type: 'url',
    provider: 'youtube',
    external_url: '',
    videoFile: null,
    thumbFile: null,
    thumbPreview: '',
    duration: '',
    status: 'active'
});

const errors = reactive({
    title: '', description: '', provider: '', external_url: '', video: ''
});

const providerOptions = [
    { id: 'youtube', name: 'YouTube' },
    { id: 'vimeo', name: 'Vimeo' },
    { id: 'mp4', name: t('videos.provider-mp4', 'Direct link (MP4)') }
];

const statusOptions = [
    { id: 'active', name: t('common.active', 'Active') },
    { id: 'inactive', name: t('common.inactive', 'Inactive') }
];

const categoryOptions = computed(() => categories.value.map(c => ({ id: c.id, name: c.name })));

const fetchCategories = async () => {
    categoriesLoading.value = true;
    try {
        const response = await appApi.forms.categories('video');
        categories.value = response.data || [];
    } catch (error) {
        console.error('Error fetching categories:', error);
    } finally {
        categoriesLoading.value = false;
    }
};

// ── file handlers ──
const triggerVideoInput = () => videoInput.value?.click();
const handleVideoSelect = (e) => { const f = e.target.files[0]; if (f) form.videoFile = f; };
const handleVideoDrop = (e) => { isDragging.value = false; const f = e.dataTransfer.files[0]; if (f && f.type.startsWith('video/')) form.videoFile = f; };

const triggerThumbInput = () => thumbInput.value?.click();
const processThumb = (file) => {
    if (file.size > 2 * 1024 * 1024) { useAlert(t('videos.thumb-too-large', 'Thumbnail must be under 2MB'), 'warning'); return; }
    form.thumbFile = file;
    const reader = new FileReader();
    reader.onload = (e) => { form.thumbPreview = e.target.result; };
    reader.readAsDataURL(file);
};
const handleThumbSelect = (e) => { const f = e.target.files[0]; if (f) processThumb(f); };
const handleThumbDrop = (e) => { isDraggingThumb.value = false; const f = e.dataTransfer.files[0]; if (f && f.type.startsWith('image/')) processThumb(f); };
const removeThumb = () => { form.thumbFile = null; form.thumbPreview = ''; if (thumbInput.value) thumbInput.value.value = ''; };

// ── prefill on edit ──
const applyVideo = (v) => {
    if (!v) return;
    form.title = v.title || '';
    form.description = v.description || '';
    form.categories = (v.categories || []).map(c => ({ id: c.id, name: c.name }));
    form.source_type = v.source_type || 'url';
    form.provider = v.provider || 'youtube';
    form.external_url = v.external_url || '';
    form.duration = v.duration || '';
    form.status = v.status || 'active';
    form.thumbPreview = v.thumbnail_url || '';
    existingFile.value = v.source_type === 'upload' && !!v.playback_url;
};

watch(() => props.video, (v) => applyVideo(v), { immediate: true });

const validate = () => {
    Object.keys(errors).forEach(k => errors[k] = '');
    let ok = true;
    if (!form.title.trim()) { errors.title = t('videos.err-title', 'Title is required'); ok = false; }
    if (form.source_type === 'url') {
        if (!form.external_url.trim()) { errors.external_url = t('videos.err-url', 'Video URL is required'); ok = false; }
    } else {
        if (!form.videoFile && !existingFile.value) { errors.video = t('videos.err-file', 'Please upload a video file'); ok = false; }
    }
    return ok;
};

const onSubmit = () => {
    if (!validate()) { useAlert(t('videos.fix-errors', 'Please fix the form errors'), 'warning'); return; }

    const fd = new FormData();
    fd.append('title', form.title.trim());
    fd.append('description', form.description || '');
    fd.append('source_type', form.source_type);
    fd.append('status', form.status);
    if (form.duration) fd.append('duration', form.duration);

    if (form.source_type === 'url') {
        fd.append('provider', form.provider);
        fd.append('external_url', form.external_url.trim());
    } else if (form.videoFile) {
        fd.append('video', form.videoFile);
    }

    if (form.thumbFile) fd.append('thumbnail', form.thumbFile);

    const categoryIds = form.categories.map(c => c.id);
    fd.append('category_ids', JSON.stringify(categoryIds));

    emit('submit', fd);
};

// expose backend validation errors to the form
const setErrors = (backendErrors = {}) => {
    Object.keys(backendErrors).forEach(key => {
        if (Object.prototype.hasOwnProperty.call(errors, key)) {
            errors[key] = Array.isArray(backendErrors[key]) ? backendErrors[key][0] : backendErrors[key];
        }
    });
};
defineExpose({ setErrors });

onMounted(fetchCategories);
</script>

<style scoped>
</style>
